import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { SITE_URL } from "@/lib/locale";
import { jsonLdScript, breadcrumbSchema } from "@/lib/seo";

const TITLE = "Calvarius-Tools — Methodische Werkzeuge zur wirtschaftlichen Markt-Analyse";
const DESC =
  "Kostenfreie Tools für Performance-Marketing, SEO und Web-Performance. Lighthouse-Live-Check, Wirtschaftlichkeits-Rechner und mehr — substantiell, ohne Anmeldung.";
const URL_ = `${SITE_URL}/tools`;
const ALT = `${SITE_URL}/en/tools`;

export const Route = createFileRoute("/tools/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL_ },
      { property: "og:locale", content: "de_DE" },
      { property: "og:locale:alternate", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [
      { rel: "canonical", href: URL_ },
      { rel: "alternate", hrefLang: "de", href: URL_ },
      { rel: "alternate", hrefLang: "en", href: ALT },
      { rel: "alternate", hrefLang: "x-default", href: URL_ },
    ],
    scripts: [
      jsonLdScript(
        breadcrumbSchema([
          { name: "Start", path: "/" },
          { name: "Tools", path: "/tools" },
        ]),
      ),
    ],
  }),
  component: ToolsHubDE,
});

function ToolsHubDE() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="CALVARIUS-TOOLS"
          headline={<>Werkzeuge zur <em>wirtschaftlichen</em> Markt-Analyse</>}
          lead="Öffentlich nutzbare Analyse-Werkzeuge für Performance-Marketing, SEO und Web-Performance. Kostenfrei, ohne Anmeldung."
          breadcrumbs={[{ label: "Start", to: "/" }, { label: "Tools" }]}
        />

        <Section number="02" kicker="Tools" headline={<>Drei Werkzeuge, <em>live im Einsatz.</em></>} variant="tinted">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {/* Card 1 — Lighthouse (active) */}
            <Link
              to="/tools/lighthouse-check"
              className="group flex h-full flex-col gap-5 rounded-lg border border-rule bg-white p-8 transition-all hover:-translate-y-0.5 hover:border-cyan hover:shadow-[0_12px_40px_-20px_oklch(0.6_0.11_225/0.4)]"
            >
              <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">Live</div>
              <h3 className="font-display text-2xl text-ink leading-tight">Lighthouse-Live-Check</h3>
              <p className="text-[15px] leading-relaxed text-ink-2">
                Echter Lighthouse-Audit Ihrer Website in unter einer Minute. Vier Dimensionen, Core Web Vitals, plus drei konkrete Verbesserungs-Hebel in unserer methodischen Einordnung.
              </p>
              <span className="mt-auto pt-4 font-mono text-xs uppercase tracking-wider text-ink-2 transition-colors group-hover:text-cyan-deep">
                Tool öffnen →
              </span>
            </Link>

            {/* Card 2 — Profitability Calculator (live) */}
            <Link
              to="/tools/wirtschaftlichkeitsrechner"
              className="group flex h-full flex-col gap-5 rounded-lg border border-rule bg-white p-8 transition-all hover:-translate-y-0.5 hover:border-cyan hover:shadow-[0_12px_40px_-20px_oklch(0.6_0.11_225/0.4)]"
            >
              <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">Live</div>
              <h3 className="font-display text-2xl text-ink leading-tight">Wirtschaftlichkeits-Rechner Performance-Marketing</h3>
              <p className="text-[15px] leading-relaxed text-ink-2">
                Berechnet aus Bestellwert, Marge und Conversion-Rate die wirtschaftlich tragfähigen CPC-Schwellen für Performance-Marketing — plus Branchen-Reality-Check.
              </p>
              <span className="mt-auto pt-4 font-mono text-xs uppercase tracking-wider text-ink-2 transition-colors group-hover:text-cyan-deep">
                Tool öffnen →
              </span>
            </Link>

            {/* Card 3 — PDF Checklist (live) */}
            <Link
              to="/tools/performance-marketing-checkliste"
              className="group flex h-full flex-col gap-5 rounded-lg border border-rule bg-white p-8 transition-all hover:-translate-y-0.5 hover:border-cyan hover:shadow-[0_12px_40px_-20px_oklch(0.6_0.11_225/0.4)]"
            >
              <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">Live · PDF</div>
              <h3 className="font-display text-2xl text-ink leading-tight">Performance-Marketing-Checkliste für KMU</h3>
              <p className="text-[15px] leading-relaxed text-ink-2">
                Methodische Setup-Sequenz in sieben Phasen. PDF-Download.
              </p>
              <span className="mt-auto pt-4 font-mono text-xs uppercase tracking-wider text-ink-2 transition-colors group-hover:text-cyan-deep">
                Checkliste öffnen →
              </span>
            </Link>
          </div>
        </Section>

        <Section number="03" kicker="Ausblick" headline={<>Weitere Tools <em>in Vorbereitung.</em></>}>
          <div className="max-w-2xl">
            <p className="mb-6 text-[17px] leading-relaxed text-ink-2">
              Weitere Calvarius-Tools sind in Vorbereitung:
            </p>
            <ul className="space-y-3 text-[15px] text-ink-2">
              <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span>AI-SEO-Sichtbarkeits-Check für ChatGPT, Perplexity, Claude</li>
              <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span>Weitere Werkzeuge aus konkreten Mandat-Bedarfen</li>
            </ul>
          </div>
        </Section>

        <Section number="04" kicker="Hinweis" headline={<>Sie suchen <em>Calvarius-Produkte?</em></>}>
          <div className="max-w-2xl">
            <p className="text-[17px] leading-relaxed text-ink-2">
              Calvarius betreibt auch eigene Software-Produkte für Mandate-Kunden — <strong className="font-semibold text-ink">Delta</strong>, <strong className="font-semibold text-ink">picazzoo</strong> und <strong className="font-semibold text-ink">GonzalAds</strong>. Diese sind gesondert dokumentiert unter{" "}
              <Link to="/leistungen/tools" className="text-cyan-deep underline decoration-cyan/40 underline-offset-4 hover:decoration-cyan-deep">
                Calvarius-Produkte
              </Link>.
            </p>
          </div>
        </Section>
      </main>
      <Footer />
    </>
  );
}
